import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ASSETS, VENTURES } from "@/lib/brand";
import VentureCard from "@/components/VentureCard";
import FeaturedIn from "@/components/FeaturedIn";
import Newsletter from "@/components/Newsletter";
import Pillars from "@/components/Pillars";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

export default function Home() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then(r => setArticles(r.data || [])).catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="/" />
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={ASSETS.portrait1}
            alt="Miguel Santana"
            className="w-full h-full object-cover object-[center_15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15] via-[#050A15]/70 to-[#050A15]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-transparent to-transparent" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-between pt-32 pb-16">
          <div className="rise rise-delay-1">
            <p className="font-overline text-silver-muted mb-4">MPR Ventures · Est. New York</p>
          </div>

            <div className="max-w-3xl">
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter text-silver rise rise-delay-2">
                Operator.
                <br />
                <span className="italic font-light text-champagne">Founder.</span>
                <br />
                Builder.
              </h1>
              <p className="mt-10 max-w-xl font-body text-base md:text-lg text-silver-muted rise rise-delay-3">
                Dominican-American entrepreneur, media personality, aspiring author, and founder
                of MPR Ventures — a multi-city American conglomerate exploring the intersection
                of business, culture, sports, and opportunity.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-5 rise rise-delay-4">
                <Link to="/ventures" data-testid="hero-cta-ventures" className="btn-editorial btn-editorial-filled">
                  The Conglomerate
                </Link>
                <Link to="/contact" data-testid="hero-cta-contact" className="btn-editorial">
                  Direct Inquiries
                </Link>
              </div>

              <div className="mt-16 rise rise-delay-5 max-w-[28rem]">
                <div className="flex items-center gap-6">
                  <div className="h-px w-16 bg-silver-muted"></div>
                  <p className="font-overline text-silver-muted">Volume I — 2025</p>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between gap-8">
              <p className="font-overline text-silver-muted hidden md:block">A Private Holding · MPR Ventures</p>
              <p className="font-overline text-silver-muted hidden md:block">Scroll ↓</p>
            </div>
        </div>
      </section>

      {/* MARQUEE — Cities */}
      <section className="border-y hairline bg-navy-surface overflow-hidden py-6" data-testid="marquee-section">
        <div className="flex marquee-track whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-16 px-8">
              {VENTURES.map((v) => (
                <div key={`${dup}-${v.id}`} className="flex items-center gap-4">
                  <span className="font-display text-2xl md:text-3xl text-silver italic">{v.city}</span>
                  <span className="font-overline text-silver-muted">{v.name}</span>
                  <span className="h-1 w-1 rounded-full bg-champagne mx-4"></span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <FeaturedIn />

      {/* MPR VENTURES INTRO */}
      <section className="relative py-32 md:py-48 px-6 md:px-12" data-testid="mpr-intro-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
            <div className="col-span-12 md:col-span-5">
              <p className="font-overline text-champagne mb-6">— The Parent Company</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tighter text-silver">
                MPR Ventures
              </h2>
              <p className="mt-6 font-body text-silver-muted text-sm uppercase tracking-[0.2em]">
                A holding firm. A philosophy. A platform.
              </p>
            </div>
            <div className="col-span-12 md:col-span-7 md:pl-12 md:border-l hairline">
              <p className="font-body text-lg md:text-xl text-silver leading-relaxed">
                MPR Ventures is the parent enterprise behind a portfolio of five operating
                companies — each headquartered in a city that defines a discipline. Hospitality in
                Las Vegas. Brands in Los Angeles. Capital in New York. Cross-border culture in
                Miami. Civic impact in Washington.
              </p>
              <p className="mt-8 font-body text-silver-muted leading-relaxed">
                The result is a privately held conglomerate built for the long arc — patient
                capital, operator-led businesses, and a singular standard of execution.
              </p>
            </div>
          </div>

          {/* Pillars */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border hairline">
            {[
              { k: "05", v: "Operating Companies" },
              { k: "05", v: "American Cities" },
              { k: "01", v: "Founder" },
              { k: "∞", v: "Patient Capital" },
              { k: "MPR", v: "Parent Holding" },
            ].map((p) => (
              <div key={p.v} className="bg-navy p-6 md:p-10">
                <p className="font-display text-3xl md:text-5xl text-silver tracking-tighter">{p.k}</p>
                <p className="mt-3 font-overline text-silver-muted">{p.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE THREEE'S — Brand Pillars */}
      <Pillars />

      {/* CONGLOMERATE GRID */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-navy-surface" data-testid="ventures-grid-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <p className="font-overline text-champagne mb-4">— The Conglomerate</p>
              <h2 className="font-display text-4xl md:text-6xl text-silver tracking-tighter leading-[1.05]">
                Five cities.<br />Five disciplines.
              </h2>
            </div>
            <Link to="/ventures" data-testid="ventures-grid-cta" className="btn-editorial self-start md:self-auto">
              View All Ventures
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {VENTURES.map((v, i) => (
              <VentureCard key={v.id} venture={v} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASE */}
      <section className="relative py-32 md:py-48 px-6 md:px-12" data-testid="about-tease-section">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8 md:gap-16 items-center">
          <div className="col-span-12 md:col-span-5 relative">
            <div className="relative aspect-[3/4] border hairline overflow-hidden">
              <img src={ASSETS.portrait2} alt="Miguel Santana" className="w-full h-full object-cover" />
              <div className="absolute inset-0 grain"></div>
            </div>
            <div className="absolute -bottom-4 -right-4 px-4 py-2 bg-navy border hairline-strong">
              <p className="font-overline text-silver">Portrait 02</p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 md:pl-12">
            <p className="font-overline text-champagne mb-6">— Profile</p>
            <h2 className="font-display text-3xl md:text-5xl text-silver leading-tight tracking-tight">
              From Santo Domingo,<br />
              <span className="italic">to building the future.</span>
            </h2>
            <p className="mt-8 font-body text-silver-muted leading-relaxed max-w-xl">
              Dominican-American entrepreneur, media personality, and founder. Through writing,
              podcasts, business ventures, and creative projects, Miguel Santana explores the
              intersection of ambition, identity, entrepreneurship, and modern society.
            </p>
            <div className="mt-10">
              <Link to="/about" data-testid="about-tease-cta" className="btn-editorial">
                Read the Biography
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PHILANTHROPY HIGHLIGHT */}
      <section className="relative min-h-[80vh] py-32 px-6 md:px-12 overflow-hidden" data-testid="philanthropy-section">
        <div className="absolute inset-0">
          <img src={ASSETS.portrait3} alt="" className="w-full h-full object-cover object-[center_20%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15] via-[#050A15]/85 to-[#050A15]/40" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto">
          <div className="max-w-2xl">
            <p className="font-overline text-champagne mb-6">— Civic Impact</p>
            <h2 className="font-display text-4xl md:text-6xl text-silver leading-[1.05] tracking-tighter">
              The Better Tomorrow Initiative
            </h2>
            <p className="mt-6 font-overline text-silver-muted">Washington · District of Columbia</p>
            <p className="mt-8 font-body text-lg text-silver leading-relaxed">
              The philanthropic engine of MPR Ventures. A Washington-based commitment to funding
              opportunity, partnering with policy, and investing in the next generation of
              American leaders.
            </p>
            <div className="mt-10">
              <Link to="/ventures#better-tomorrow" data-testid="philanthropy-cta" className="btn-editorial">
                Initiative Overview
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS PREVIEW */}
      <section className="py-32 px-6 md:px-12 bg-navy-surface" data-testid="press-preview-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-16 gap-8 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-4">— Selected Press</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter">
                From the Journal
              </h2>
            </div>
            <Link to="/journal" data-testid="press-preview-cta" className="btn-editorial">
              All Articles
            </Link>
          </div>

          <div className="border-t hairline">
            {articles.slice(0, 3).map((a, i) => (
              <Link
                key={a.id}
                to={`/journal/${a.slug || a.id}`}
                data-testid={`press-preview-item-${i}`}
                className="group grid grid-cols-12 gap-6 items-center py-8 border-b hairline hover:bg-white/[0.02] transition-colors duration-300 px-2"
              >
                <div className="col-span-12 md:col-span-2">
                  <p className="font-overline text-silver-muted">{a.publication || "Editorial"}</p>
                </div>
                <div className="col-span-12 md:col-span-8">
                  <h3 className="font-display text-2xl md:text-3xl text-silver leading-tight tracking-tight group-hover:text-champagne transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-2 font-body text-silver-muted text-sm line-clamp-2">{a.excerpt}</p>
                </div>
                <div className="col-span-12 md:col-span-2 text-left md:text-right">
                  <span className="font-overline text-silver group-hover:text-champagne transition-colors">Read →</span>
                </div>
              </Link>
            ))}
            {articles.length === 0 && (
              <p className="py-12 font-body text-silver-muted">No articles published yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* CONTACT TEASE */}
      <section className="py-32 px-6 md:px-12" data-testid="contact-tease-section">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8 md:gap-16 items-center">
          <div className="col-span-12 md:col-span-7">
            <p className="font-overline text-champagne mb-6">— For Direct Inquiries</p>
            <h2 className="font-display text-5xl md:text-7xl text-silver leading-[0.95] tracking-tighter">
              Bookings, press,<br />
              <span className="italic text-champagne">partnerships.</span>
            </h2>
            <p className="mt-8 max-w-xl font-body text-silver-muted text-lg leading-relaxed">
              Inquiries for speaking engagements, brand partnerships, philanthropic collaborations,
              and press are routed through the Office of Miguel Santana.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-12 md:border-l hairline">
            <p className="font-overline text-silver-muted mb-4">Office</p>
            <p className="font-display text-2xl text-silver mb-1">office@iammiguelsantana.com</p>
            <p className="font-body text-silver-muted text-sm">Reply within 3 business days.</p>
            <div className="mt-10">
              <Link to="/contact" data-testid="contact-tease-cta" className="btn-editorial btn-editorial-filled">
                Open Booking Form
              </Link>
            </div>

            <div className="mt-12 pt-10 border-t hairline">
              <p className="font-overline text-champagne mb-3">— The Dispatch</p>
              <p className="font-body text-silver text-base mb-6">
                A short, occasional newsletter from the Office of Miguel Santana — new ventures,
                podcast drops, and press features. No noise.
              </p>
              <Newsletter source="home" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
